import TenantComponents from '@components';
import tenantConstants from '@constants';
import tenantRoutes from '@routes';
import tenantTheme from '@theme';
import tenantUtils from '@utils';
import { Menu } from 'antd';
import cx from 'clsx';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Button, Dropdown, Flex, Group, Heading, Icon, Select } from '../../../../components/common';
import LiteHeaderDropdown from '../../../../components/common/lite-header-dropdown/lite-header-dropdown';
import NavBarNavigationContainer from './navBarNavigationContainer';
import { DropdownItemUpsell, HeaderStyled, ModalPopupStyle } from './styled';
import { areaRangeUnitList, currencyArabicConversionUnitList, getCookieUnits, getLink } from './utils';
import { getLoginPath } from '../../../../utility/utility';

export const DeskTopNavBar = ({
  userData,
  logoutUser,
  locale,
  savedSearches,
  setSavedSearches,
  favouriteProperties,
  showChangeCurrencyModal,
  setShowChangeCurrencyModal,
  showChangeAreaUnitModal,
  setShowChangeAreaUnitModal,
  saveSettings,
  pathLocale,
  toggleLanguage,
  languageObj,
  isFavpropLoading,
  setNoFavPropModal,
  onDeleteSavedSearch,
  setNoSaveSearchModal,
  isAuthenticated,
  fullWidth = false,
  hideLoginButton = false,
  hideSavedSearches = false,
  hideFavouriteProperties = false,
  hideMyListings = false,
  hidePostAd = false,
  hideCurrencyConverter = false,
}) => {
  const { t } = useTranslation();
  const units = getCookieUnits(t);
  const [toSetCurrency, setToSetCurrency] = useState(units.currency);
  const [toSetArea, setToSetArea] = useState(units.area);
  const [modalVisible, setModalVisible] = useState(false);
  const { user } = useSelector((state) => state.app.loginUser);

  const options = useMemo(
    () => [
      {
        value: getLink(t('Become a Business User'), tenantRoutes.app('', false, user)?.prop_shop?.path),
        key: t('Become a Business User'),
      },
      {
        value: getLink(t('Account Settings'), `${tenantRoutes.app('', false, user).settings.route}/user-profile`),
        href: `${tenantRoutes.app('', false, user).settings.route}/user-profile`,
        key: t('Account Settings'),
      },
      {
        value: t('Logout'),
        key: t('Logout'),
        onClick: () => logoutUser(),
      },
    ],
    [],
  );

  const settingsOptions = useMemo(
    () => [
      { value: t('Change Currency'), onClick: () => setShowChangeCurrencyModal(true) },
      { value: t('Change Area Unit'), onClick: () => setShowChangeAreaUnitModal(true) },
    ],
    [],
  );

  const onCancelSettingPopup = () => {
    if (showChangeCurrencyModal) {
      setShowChangeCurrencyModal(false);
    }
    if (showChangeAreaUnitModal) {
      setShowChangeAreaUnitModal(false);
    }
  };

  const onSaveUnit = () => {
    setShowChangeCurrencyModal(false);
    setShowChangeAreaUnitModal(false);
    const settings = showChangeCurrencyModal ? { currency: toSetCurrency } : { area: toSetArea };
    saveSettings(settings);
  };

  const renderAreaUnitSelector = () => {
    return (
      <div onClick={(event) => event.stopPropagation()}>
        <Select
          suffixIcon="IoMdArrowDropdown"
          openIcon="IoMdArrowDropdown"
          className="unitSelectDropdown"
          value={toSetArea}
          unit={toSetArea}
          onChange={(value) => setToSetArea(value)}
          style={{ alignItems: 'start' }}
          containerClass="unitSelect"
          options={areaRangeUnitList}
          getOptionValue={(e) => e.slug}
          getOptionLabel={(e) => t(e.alt_label)}
        />
      </div>
    );
  };

  const renderChangeCurrencySelector = () => {
    return (
      <div onClick={(event) => event.stopPropagation()}>
        <Select
          suffixIcon="IoMdArrowDropdown"
          openIcon="IoMdArrowDropdown"
          className="unitSelectDropdown"
          unit={toSetCurrency}
          onChange={(value) => setToSetCurrency(value)}
          type="price"
          style={{ alignItems: 'start' }}
          options={currencyArabicConversionUnitList}
          value={toSetCurrency}
          getOptionValue={(e) => e.slug}
          getOptionLabel={(e) => e.alt_label}
        />
      </div>
    );
  };

  const renderSettingsPopup = () => {
    return (
      <ModalPopupStyle
        closeIcon={<Icon icon="CgClose" color="#222" size={20} />}
        className="modalHeaderContainer"
        width={400}
        visible={showChangeCurrencyModal || showChangeAreaUnitModal}
        style={{ overlay: { zIndex: 1000 } }}
        onCancel={onCancelSettingPopup}
        footer={null}
        title={
          <Heading as="h2" className="text-center mb-0">
            {showChangeCurrencyModal ? t('Change Currency') : showChangeAreaUnitModal && t('Change Area')}
          </Heading>
        }
      >
        {showChangeCurrencyModal ? (
          renderChangeCurrencySelector()
        ) : showChangeAreaUnitModal ? (
          renderAreaUnitSelector()
        ) : (
          <></>
        )}
        <Button className="saveBtn" type="success" block onClick={onSaveUnit}>
          {t('SAVE')}
        </Button>
      </ModalPopupStyle>
    );
  };

  const renderFavPropertyDropdownUI = () => {
    if (hideFavouriteProperties) return null;

    return (
      <Button
        type="link"
        {...(favouriteProperties.length > 0
          ? { href: `${pathLocale}/favorite-properties.html` }
          : { onClick: () => setNoFavPropModal(true) })}
        className={cx('px-0 favouritePropertiesTag navbar-text')}
        icon="IconFavourite"
        iconSize="1.28571em"
        iconColor="#4c4a4a"
      >
        <div>
          {t('Favourite properties')}
          {!isFavpropLoading && (
            <span className="text-primary fw-400" style={{ marginInlineStart: '3px' }}>
              ({favouriteProperties.length || 0})
            </span>
          )}
        </div>
      </Button>
    );
  };

  const getSavedSearchesDropDownUI = () => {
    if (hideSavedSearches) return null;

    return (
      <LiteHeaderDropdown
        title={
          <div className="navbar-text">
            {t('Saved searches')}
            {!savedSearches?.loading && (
              <span
                className="text-primary fw-400"
                style={{ marginInlineStart: '3px', color: tenantTheme['primary-color'] }}
              >
                ({savedSearches?.length || 0})
              </span>
            )}
          </div>
        }
        placement={locale === 'ar' ? 'bottomLeft' : 'bottomRight'}
        prefixIcon="TiStar"
        options={savedSearches}
        optionsIcon={'IconTrashCan'}
        color="#4c4a4a"
        width={13}
        className="pointer"
        onOptionsIconClick={onDeleteSavedSearch}
      />
    );
  };

  const renderLoginDropDownUI = () => {
    if (hideLoginButton) return null;

    return isAuthenticated ? (
      <Dropdown
        options={options}
        placement={locale === 'ar' ? 'bottomLeft' : 'bottomRight'}
        prefixIcon="IconLogin"
        prefixIconProps={{ size: '1.28571em', color: '#4c4a4a' }}
        iconSize="1.28571em"
        className={cx('dropDownSelect', 'languageDropdown', 'userDropdown', 'navbar-text')}
        getOptionLabel={(e) => e?.value}
        getOptionValue={(e) => e?.value}
        suffixIcon="IoMdArrowDropdown"
        suffixIconProps={{ color: '#4c4a4a', size: '1.4em', style: { marginInline: -5, marginBlockStart: 1 } }}
        style={{ gap: 8, padding: 0 }}
        content={
          <>
            <DropdownItemUpsell>
              <Link to={tenantRoutes.app('', false, user)?.prop_shop?.path}>
                <div className="navbar-text-bold">{t('Are you a Broker/Agency?')}</div>
                <Flex className="navbar-text-small">{t('Get a Business Package')}</Flex>
              </Link>
            </DropdownItemUpsell>
            {options
              .filter((e) => e.key !== t('Become a Business User'))
              .map((e) =>
                e.onClick ? (
                  <Menu.Item key={e?.key} style={{ ...e.style }} onClick={() => e?.onClick()}>
                    <Group template="max-content auto" gap="8px" style={{ alignItems: 'center' }}>
                      {e?.icon && <Icon icon={e.icon} size="1.2em" />}
                      <span className="navbar-text">{e.key}</span>
                    </Group>
                  </Menu.Item>
                ) : (
                  <Menu.Item key={e?.key} className="p-0">
                    <Link to={e?.href}>
                      <Group template="max-content auto" gap="0px" style={{ alignItems: 'center' }}>
                        {e?.icon && <Icon icon={e.icon} size="1.2em" />}
                        <span className="navbar-text">{e?.key}</span>
                      </Group>
                    </Link>
                  </Menu.Item>
                ),
              )}
          </>
        }
      >
        <span className="navbar-text" style={{ color: '#222' }}>
          {tenantUtils.getLocalisedString(userData, 'name')}
        </span>
      </Dropdown>
    ) : (
      <Button
        className="u-p0 navbar-text"
        size="sm"
        variant="link"
        href={getLoginPath()}
        icon="IconLogin"
        iconColor="#4c4a4a"
      >
        {t('Log In')}
      </Button>
    );
  };

  const getSiteSettingsDropDownUI = () => {
    return (
      <Dropdown
        options={settingsOptions}
        placement={locale === 'ar' ? 'bottomRight' : 'bottomLeft'}
        prefixIcon="IconSettings"
        prefixIconProps={{ size: '1.28571em', color: '#4c4a4a' }}
        rootClassName="p-0"
        className={cx('dropDownSelect', 'languageDropdown', 'fz-12', 'navbar-text')}
        getOptionValue={(item) => item?.value}
        getOptionLabel={(item) => item?.value}
        suffixIcon={null}
        style={{ gap: 7 }}
      >
        {t('Site settings')}
      </Dropdown>
    );
  };

  const renderDesktopNavUI = () => {
    return (
      <>
        <div className={`navbar-ceiling ${fullWidth ? 'full-width' : ''}`} style={{'letterSpacing': '0.1px', 'wordSpacing': '-1px'}}>
          <Flex className="container" justify="space-between" style={{ alignItems: 'center', marginBottom: '-1px' }}>
            <Flex gap="21px">
              {languageObj && (
                <a href={toggleLanguage(languageObj.key)} style={{ marginTop: '1px', marginRight: '6px' }}>
                  <Button
                    className="px-0 languageSwitcher navbar-text"
                    type="link"
                    icon="IconLanguageSwitcher"
                    iconSize="1.428571em"
                    iconColor="#4c4a4a"
                    style={{ gap: '5px' }}
                  >
                    {languageObj.labelShort}
                  </Button>
                </a>
              )}
              {!hideCurrencyConverter && getSiteSettingsDropDownUI()}
              {tenantConstants.PITCH_MOBILE_APP && (
                <>
                  <Button
                    className="px-0 navbar-text"
                    type="link"
                    icon="IconMobilePhone"
                    iconSize="16px"
                    iconColor="#4c4a4a"
                    onClick={() => {
                      setModalVisible(true);
                    }}
                    style={{ gap: '6px' }}
                  >
                    {t('Download App')}
                  </Button>
                  <TenantComponents.DownloadAppModal visible={modalVisible} onClose={() => setModalVisible(false)} />
                </>
              )}
            </Flex>
            <Flex gap="22px" style={{ alignItems: 'center' }}>
              {renderFavPropertyDropdownUI()}
              {/* to be implemented yet */}

              {tenantConstants.SAVED_SEARCHES_ENABLED && !hideSavedSearches && (
                <>
                  {savedSearches?.length > 0 ? (
                    getSavedSearchesDropDownUI()
                  ) : (
                    <>
                      <Button
                        className="px-0 navbar-text"
                        onClick={setNoSaveSearchModal}
                        icon="TiStar"
                        iconSize="1.428571em"
                        type={'link'}
                        iconColor="#4c4a4a"
                      >
                        <div>
                          {t('Saved searches')}
                          {!savedSearches?.loading && (
                            <span className="text-primary fw-400" style={{ marginInlineStart: '3px' }}>
                              ({savedSearches?.length || 0})
                            </span>
                          )}
                        </div>
                      </Button>
                    </>
                  )}
                </>
              )}
              {!hideMyListings && (
                <Link to="/listings">
                  <Button className="px-0 navbar-text" type="link" icon="IconListings" iconSize="1.428571em" iconColor="#4c4a4a">
                    {t('My Listings')}
                  </Button>
                </Link>
              )}
              {renderLoginDropDownUI()}
            </Flex>
          </Flex>
        </div>
        <NavBarNavigationContainer locale={locale} fullWidth={fullWidth} hidePostAd={hidePostAd} />
      </>
    );
  };

  return (
    <HeaderStyled $fullWidth={fullWidth}>
      {renderSettingsPopup()}
      {renderDesktopNavUI()}
    </HeaderStyled>
  );
};
